import React, { useEffect, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { PDFDownloadLink } from '@react-pdf/renderer';
import { useAuth } from '../../context/AuthContext';
import { PageLayout, ActionButton } from '../../components/DesignSystem';
import POPdf from '../../components/POPdf';
import GRNPdf, { type GrnPdfData } from '../../components/GRNPdf';
import API_URL from '../../config/api';
import { apiFetch } from '../../utils/apiFetch';
import { showToast } from '../../utils/toast';
import { ListPagination, paginateList } from '../../components/ListPagination';
import '../../styles/listPage.css';

const STATUS_COLORS: Record<string, string> = {
  Draft: '#6b7280',
  Sent: '#3b82f6',
  Partial: '#f59e0b',
  Received: '#10b981',
  Cancelled: '#ef4444',
};

const PurchaseOrders: React.FC = () => {
  const { token } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  const [pos, setPos] = useState<any[]>([]);
  const [listPage, setListPage] = useState(1);
  const [suppliers, setSuppliers] = useState<any[]>([]);
  const [products, setProducts] = useState<any[]>([]);
  const [businessSettings, setBusinessSettings] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState('All');
  const [supplierFilter, setSupplierFilter] = useState('All');
  const [searchTerm, setSearchTerm] = useState('');
  const [showCreate, setShowCreate] = useState(false);
  const [showView, setShowView] = useState(false);
  const [activePO, setActivePO] = useState<any>(null);
  const [showReceive, setShowReceive] = useState(false);
  const [receiveQtys, setReceiveQtys] = useState<Record<number, string>>({});
  const [receiveCosts, setReceiveCosts] = useState<Record<number, string>>({});
  const [pendingGrn, setPendingGrn] = useState<GrnPdfData | null>(null);
  const [showLowStock, setShowLowStock] = useState(false);
  const [lowStockGroups, setLowStockGroups] = useState<any[]>([]);
  const [lowStockLoading, setLowStockLoading] = useState(false);
  const [selectedGroupKeys, setSelectedGroupKeys] = useState<Set<string>>(new Set());
  const [saving, setSaving] = useState(false);
  const [showSendEmail, setShowSendEmail] = useState(false);
  const [emailForm, setEmailForm] = useState({ supplier_email: '', message: '' });
  const [editingPoId, setEditingPoId] = useState<number | null>(null);

  const resetCreateForm = () => {
    setForm({ supplier_id: '', supplier_name: '', expected_date: '', notes: '' });
    setFormItems([{ product_id: '', product_name: '', sku: '', quantity_ordered: '', unit_cost: '' }]);
    setEditingPoId(null);
  };

  const productUnitCost = (prod: any) => {
    const cost = prod.last_purchase_price ?? prod.avg_cost ?? prod.cost;
    return cost != null && cost !== '' ? String(cost) : '';
  };

  const defaultQtyForProduct = (prod: any) => {
    const moq = parseFloat(prod.min_order_qty) || 0;
    const box = parseFloat(prod.box_quantity) || 0;
    if (moq > 0) return String(moq);
    if (box > 0) return String(box);
    return '1';
  };

  const applySupplierDefaults = (supplierId: string) => {
    const sup = suppliers.find((s: any) => String(s.id) === supplierId);
    if (!sup) return;
    const leadDays = Number(sup.lead_time_days ?? sup.leadTimeDays ?? sup.lead_time ?? 0);
    let expectedDate = '';
    if (leadDays > 0) {
      const d = new Date();
      d.setDate(d.getDate() + leadDays);
      expectedDate = d.toISOString().slice(0, 10);
    }
    setForm(f => ({
      ...f,
      supplier_id: supplierId,
      supplier_name: sup.name || '',
      expected_date: expectedDate || f.expected_date,
    }));
  };

  const initReceiveForm = (po: any) => {
    const costs: Record<number, string> = {};
    (po.items || []).forEach((item: any) => {
      costs[item.id] = String(parseFloat(item.unit_cost) || '');
    });
    setReceiveQtys({});
    setReceiveCosts(costs);
  };

  const buildGrnPdfData = (po: any, grnNumber: string, receipts: any[]): GrnPdfData => {
    const itemCostMap: Record<number, number> = {};
    (po.items || []).forEach((item: any) => {
      itemCostMap[item.id] = parseFloat(item.unit_cost) || 0;
    });
    const lines = receipts.map((r: any) => {
      const unitCost = parseFloat(r.unit_cost) || itemCostMap[r.po_item_id] || 0;
      const qty = parseFloat(r.quantity_received) || 0;
      return {
        product_name: r.product_name || 'Item',
        quantity_received: qty,
        unit_cost: unitCost,
        line_total: qty * unitCost,
      };
    });
    const total = lines.reduce((s, l) => s + l.line_total, 0);
    return {
      grn_number: grnNumber,
      po_number: po.po_number,
      supplier_name: po.supplier_name,
      received_at: receipts[0]?.created_at,
      received_by_name: receipts[0]?.received_by_name,
      lines,
      total,
    };
  };

  const groupReceiptBatches = (po: any) => {
    if (!po?.receipts?.length) return [];
    const map: Record<string, any[]> = {};
    for (const r of po.receipts) {
      const key = r.grn_number || `legacy-${String(r.created_at).slice(0, 10)}-${r.id}`;
      if (!map[key]) map[key] = [];
      map[key].push(r);
    }
    return Object.entries(map).map(([key, receipts]) => {
      const grnNumber = receipts[0].grn_number || key;
      const data = buildGrnPdfData(po, grnNumber, receipts);
      return { key, grnNumber, receipts, data };
    });
  };

  const loadLowStockSuggestions = async () => {
    setLowStockLoading(true);
    try {
      const res = await apiFetch(`${API_URL}/purchase-orders/low-stock-suggestions`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (res.ok) {
        const data = await res.json();
        setLowStockGroups(data.groups || []);
        setSelectedGroupKeys(new Set((data.groups || []).map((g: any) => String(g.supplier_id))));
        setShowLowStock(true);
      } else {
        showToast('Failed to load low-stock suggestions');
      }
    } catch {
      showToast('Network error');
    } finally {
      setLowStockLoading(false);
    }
  };

  const submitBulkDraft = async () => {
    const groups = lowStockGroups.filter((g: any) => selectedGroupKeys.has(String(g.supplier_id)));
    if (groups.length === 0) { showToast('Select at least one supplier group'); return; }

    setSaving(true);
    try {
      const res = await apiFetch(`${API_URL}/purchase-orders/bulk-draft`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ groups }),
      });
      if (res.ok) {
        const data = await res.json();
        setShowLowStock(false);
        fetchAll();
        showToast(`Created ${data.count} draft PO${data.count !== 1 ? 's' : ''} from low-stock report`);
      } else {
        const err = await res.json();
        showToast(err.error || 'Failed to create draft POs');
      }
    } catch {
      showToast('Network error');
    } finally {
      setSaving(false);
    }
  };

  const validateItemQuantities = (items: any[]) => {
    for (const item of items) {
      if (!item.product_id) continue;
      const prod = products.find((p: any) => String(p.id) === String(item.product_id));
      if (!prod) continue;
      const qty = parseFloat(item.quantity_ordered) || 0;
      const moq = parseFloat(prod.min_order_qty) || 0;
      const box = parseFloat(prod.box_quantity) || 0;
      if (moq > 0 && qty < moq) {
        return `${prod.name}: minimum order is ${moq}`;
      }
      if (box > 1 && qty % box !== 0) {
        return `${prod.name}: quantity must be in multiples of ${box} (case/box size)`;
      }
    }
    return null;
  };

  // Create form
  const [form, setForm] = useState({
    supplier_id: '',
    supplier_name: '',
    expected_date: '',
    notes: '',
  });
  const [formItems, setFormItems] = useState<any[]>([
    { product_id: '', product_name: '', sku: '', quantity_ordered: '', unit_cost: '' },
  ]);

  useEffect(() => {
    if (!token) return;
    Promise.all([
      apiFetch(`${API_URL}/suppliers`, { headers: { Authorization: `Bearer ${token}` } }),
      apiFetch(`${API_URL}/products/dropdown`, { headers: { Authorization: `Bearer ${token}` } }),
      apiFetch(`${API_URL}/business`, { headers: { Authorization: `Bearer ${token}` } }),
    ]).then(async ([supRes, prodRes, bsRes]) => {
      if (supRes.ok) {
        const sd = await supRes.json();
        setSuppliers(Array.isArray(sd) ? sd : (sd.data || sd.suppliers || []));
      }
      if (prodRes.ok) setProducts(await prodRes.json());
      if (bsRes.ok) setBusinessSettings(await bsRes.json());
    }).catch(console.error);
  }, [token]);

  // Open New PO immediately when linked from supplier (don't wait for suppliers list)
  useEffect(() => {
    const sid = new URLSearchParams(location.search).get('supplier_id');
    if (!sid) return;
    setForm(f => ({ ...f, supplier_id: sid }));
    setShowCreate(true);
    navigate('/purchase-orders', { replace: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.search]);

  useEffect(() => {
    if (form.supplier_id && suppliers.length) {
      applySupplierDefaults(String(form.supplier_id));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [suppliers, form.supplier_id]);

  useEffect(() => {
    fetchPurchaseOrders();
  }, [token]);

  useEffect(() => {
    setListPage(1);
  }, [statusFilter, supplierFilter, searchTerm]);

  const fetchPurchaseOrders = async () => {
    if (!token) return;
    setLoading(true);
    try {
      const poRes = await apiFetch(`${API_URL}/purchase-orders`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (poRes.ok) {
        const poData = await poRes.json();
        setPos(Array.isArray(poData) ? poData : (poData.data || []));
      }
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  };

  const fetchAll = async () => {
    await Promise.all([fetchPurchaseOrders()]);
  };
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const pid = params.get('product_id');
    const q = params.get('q');

    if (q) {
      setSearchTerm(q);
      navigate('/purchase-orders', { replace: true });
      return;
    }

    if (!products.length) return;
    if (!pid) return;

    const prod = products.find((p: any) => String(p.id) === String(pid));
    if (!prod) {
      showToast('Product not found for restock prefill');
      return;
    }

    const supplierId = prod.supplier_id ? String(prod.supplier_id) : (prod.supplier ? String(prod.supplier) : '');
    const supplierName = supplierId
      ? (suppliers.find((s: any) => String(s.id) === supplierId)?.name || '')
      : '';

    const leadDays = supplierId
      ? Number(suppliers.find((s: any) => String(s.id) === supplierId)?.lead_time_days ?? 0)
      : 0;
    let expectedDate = '';
    if (leadDays > 0) {
      const d = new Date();
      d.setDate(d.getDate() + leadDays);
      expectedDate = d.toISOString().slice(0, 10);
    }

    setForm({ supplier_id: supplierId, supplier_name: supplierName, expected_date: expectedDate, notes: `Restock request for ${prod.name}` });
    setFormItems([
      {
        product_id: String(prod.id),
        product_name: prod.name || '',
        sku: prod.sku || '',
        quantity_ordered: defaultQtyForProduct(prod),
        unit_cost: productUnitCost(prod),
      },
    ]);
    setShowCreate(true);
    navigate('/purchase-orders', { replace: true });
  }, [location.search, products, suppliers]);

  const openView = async (po: any) => {
    try {
      const res = await apiFetch(`${API_URL}/purchase-orders/${po.id}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (res.ok) {
        const data = await res.json();
        setActivePO(data);
        initReceiveForm(data);
        // Find supplier email if available
        const supplier = suppliers.find(s => String(s.id) === String(data.supplier_id));
        const supplierEmail = supplier?.email || data.supplier_name || '';
        setEmailForm({ supplier_email: supplierEmail, message: '' });
        setShowView(true);
      }
    } catch (e) {
      console.error(e);
    }
  };

  const openEditDraft = async (po: any) => {
    try {
      const res = await apiFetch(`${API_URL}/purchase-orders/${po.id}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) {
        showToast('Failed to load PO for editing');
        return;
      }
      const data = await res.json();
      if (data.status !== 'Draft') {
        showToast('Only Draft POs can be edited');
        return;
      }
      setForm({
        supplier_id: data.supplier_id ? String(data.supplier_id) : '',
        supplier_name: data.supplier_name || '',
        expected_date: data.expected_date ? String(data.expected_date).slice(0, 10) : '',
        notes: data.notes || '',
      });
      setFormItems(
        (data.items || []).map((item: any) => ({
          product_id: item.product_id ? String(item.product_id) : '',
          product_name: item.product_name || '',
          sku: item.sku || '',
          quantity_ordered: String(parseFloat(item.quantity_ordered) || ''),
          unit_cost: String(parseFloat(item.unit_cost) || ''),
        }))
      );
      setEditingPoId(data.id);
      setShowView(false);
      setShowCreate(true);
    } catch (e) {
      showToast('Failed to load PO');
    }
  };

  const submitCreate = async () => {
    const validItems = formItems.filter(i => i.product_name && parseFloat(i.quantity_ordered) > 0);
    if (validItems.length === 0) { showToast('Add at least one item with a name and quantity'); return; }

    const qtyError = validateItemQuantities(validItems);
    if (qtyError) { showToast(qtyError); return; }

    setSaving(true);
    try {
      const supplierId = form.supplier_id ? parseInt(form.supplier_id) : null;
      const supplierName = supplierId
        ? (suppliers.find(s => String(s.id) === form.supplier_id)?.name || form.supplier_name)
        : form.supplier_name;

      const payload = {
        supplier_id: supplierId,
        supplier_name: supplierName,
        expected_date: form.expected_date || null,
        notes: form.notes || '',
        items: validItems.map(i => ({
          product_id: i.product_id || null,
          product_name: i.product_name,
          sku: i.sku || '',
          quantity_ordered: parseFloat(i.quantity_ordered) || 0,
          unit_cost: parseFloat(i.unit_cost) || 0,
        })),
      };

      const url = editingPoId
        ? `${API_URL}/purchase-orders/${editingPoId}`
        : `${API_URL}/purchase-orders`;
      const res = await apiFetch(url, {
        method: editingPoId ? 'PUT' : 'POST',
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });
      if (res.ok) {
        setShowCreate(false);
        resetCreateForm();
        fetchAll();
        showToast(editingPoId ? 'PO updated' : 'PO created');
      } else {
        const err = await res.json();
        showToast(err.error || (editingPoId ? 'Failed to update PO' : 'Failed to create PO'));
      }
    } catch (e) {
      showToast('Network error');
    } finally {
      setSaving(false);
    }
  };

  const submitReceive = async () => {
    if (!activePO) return;
    const receivedItems = activePO.items
      .map((item: any) => ({
        item_id: item.id,
        quantity_received: parseFloat(receiveQtys[item.id] || '0'),
        unit_cost: receiveCosts[item.id] !== undefined ? parseFloat(receiveCosts[item.id] || '0') : undefined,
      }))
      .filter((r: any) => r.quantity_received > 0);
    if (receivedItems.length === 0) { showToast('Enter at least one quantity to receive'); return; }

    setSaving(true);
    try {
      const res = await apiFetch(`${API_URL}/purchase-orders/${activePO.id}/receive`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ receivedItems }),
      });
      if (res.ok) {
        const result = await res.json();
        const grnData: GrnPdfData = {
          grn_number: result.grn_number,
          po_number: activePO.po_number,
          supplier_name: activePO.supplier_name,
          received_at: new Date().toISOString(),
          received_by_name: result.received_by_name,
          lines: (result.grn_lines || []).map((l: any) => ({
            product_name: l.product_name,
            sku: l.sku,
            quantity_received: l.quantity_received,
            unit_cost: l.unit_cost,
            line_total: l.quantity_received * l.unit_cost,
          })),
          total: result.grn_total || 0,
        };
        setPendingGrn(grnData);
        setShowReceive(false);
        setShowView(false);
        fetchAll();
        showToast(`✅ Received — ${result.grn_number} created`);
      } else {
        const err = await res.json();
        showToast(err.error || 'Failed to receive items');
      }
    } catch (e) {
      showToast('Network error');
    } finally {
      setSaving(false);
    }
  };

  const submitSendEmail = async () => {
    if (!activePO) return;
    if (!emailForm.supplier_email.trim()) {
      showToast('Please enter supplier email');
      return;
    }

    setSaving(true);
    try {
      const res = await apiFetch(`${API_URL}/purchase-orders/${activePO.id}/send-email`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({
          supplier_email: emailForm.supplier_email.trim(),
          message: emailForm.message.trim() || null,
        }),
      });
      if (res.ok) {
        const result = await res.json();
        showToast('✅ PO sent successfully!');
        setShowSendEmail(false);
        setEmailForm({ supplier_email: '', message: '' });
        if (result.status && activePO.status === 'Draft') {
          setActivePO((prev: any) => prev ? { ...prev, status: result.status } : prev);
        }
        fetchAll();
      } else {
        const err = await res.json();
        showToast(err.error || 'Failed to send PO');
      }
    } catch (e) {
      showToast('Network error');
    } finally {
      setSaving(false);
    }
  };

  const submitCancel = async () => {
    if (!activePO) return;
    if (!window.confirm('Are you sure you want to cancel this PO? Any received stock will be reversed.')) {
      return;
    }

    setSaving(true);
    try {
      const res = await apiFetch(`${API_URL}/purchase-orders/${activePO.id}/cancel`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
      });
      if (res.ok) {
        const result = await res.json();
        showToast('✅ PO cancelled' + (result.message ? ': ' + result.message : ''));
        setShowView(false);
        fetchAll();
      } else {
        const err = await res.json();
        showToast(err.error || 'Failed to cancel PO');
      }
    } catch (e) {
      showToast('Network error');
    } finally {
      setSaving(false);
    }
  };

  const cancelPOFromList = async (po: any) => {
    if (po.status === 'Draft') {
      return deletePO(po);
    }
    if (!window.confirm('Cancel this PO? Any received stock will be reversed.')) return;

    setSaving(true);
    try {
      const res = await apiFetch(`${API_URL}/purchase-orders/${po.id}/cancel`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
      });
      if (res.ok) {
        const result = await res.json();
        showToast('✅ PO cancelled' + (result.message ? ': ' + result.message : ''));
        fetchAll();
      } else {
        const err = await res.json();
        showToast(err.error || 'Failed to cancel PO');
      }
    } catch (e) {
      showToast('Network error');
    } finally {
      setSaving(false);
    }
  };

  const updateStatus = async (po: any, newStatus: string) => {
    if (!confirm(`Change status to "${newStatus}"?`)) return;
    await apiFetch(`${API_URL}/purchase-orders/${po.id}`, {
      method: 'PUT',
      headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
      body: JSON.stringify({ status: newStatus }),
    });
    fetchAll();
  };

  const deletePO = async (po: any) => {
    if (!confirm(`Delete PO ${po.po_number}? This cannot be undone.`)) return;
    await apiFetch(`${API_URL}/purchase-orders/${po.id}`, {
      method: 'DELETE',
      headers: { Authorization: `Bearer ${token}` },
    });
    fetchAll();
  };

  const setItemField = (idx: number, field: string, value: string) => {
    setFormItems(prev => {
      const next = [...prev];
      next[idx] = { ...next[idx], [field]: value };
      return next;
    });
  };

  const selectProduct = (idx: number, productId: string) => {
    const prod = products.find((p: any) => String(p.id) === productId);
    if (prod) {
      setFormItems(prev => {
        const next = [...prev];
        next[idx] = {
          ...next[idx],
          product_id: String(prod.id),
          product_name: prod.name,
          sku: prod.sku || '',
          unit_cost: productUnitCost(prod),
          quantity_ordered: next[idx].quantity_ordered || defaultQtyForProduct(prod),
        };
        return next;
      });
    } else {
      setItemField(idx, 'product_id', '');
    }
  };

  const filtered = pos.filter((p: any) => {
    const matchesStatus = statusFilter === 'All' || p.status === statusFilter;
    const matchesSupplier =
      supplierFilter === 'All' ||
      String(p.supplier_id || '') === supplierFilter ||
      String((suppliers.find((s: any) => s.name === p.supplier_name) as any)?.id || '') === supplierFilter;
    const q = searchTerm.trim().toLowerCase();
    const matchesSearch = !q ||
      String(p.po_number || '').toLowerCase().includes(q) ||
      String(p.supplier_name || '').toLowerCase().includes(q) ||
      String(p.notes || '').toLowerCase().includes(q);
    return matchesStatus && matchesSupplier && matchesSearch;
  });

  const { pages: poPages, safePage: safeListPage, slice: paginatedPOs } =
    paginateList(filtered, listPage);

  // Products filtered to the selected supplier in the create form
  // products.supplier stores the supplier ID as text (FK relationship)
  const supplierLinkedProducts = form.supplier_id
    ? products.filter((p: any) =>
        String(p.supplier_id || p.supplier || '') === String(form.supplier_id)
      )
    : products;
  // Fall back to all products if none are linked to this supplier yet
  const itemProductList = form.supplier_id && supplierLinkedProducts.length > 0
    ? supplierLinkedProducts
    : products;
  const selectedSupplierName = form.supplier_id
    ? (suppliers.find((s: any) => String(s.id) === form.supplier_id) as any)?.name ?? ''
    : '';
  const selectedSupplier = form.supplier_id
    ? suppliers.find((s: any) => String(s.id) === form.supplier_id)
    : null;
  const selectedPaymentTerms = selectedSupplier?.payment_terms || selectedSupplier?.paymentTerms || '';

  // KPI metrics for purchasing decisions
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const openValue = pos
    .filter((p: any) => p.status !== 'Received' && p.status !== 'Cancelled')
    .reduce((sum: number, p: any) => sum + Number(p.total || 0), 0);
  const partialValue = pos
    .filter((p: any) => p.status === 'Partial')
    .reduce((sum: number, p: any) => sum + Number(p.total || 0), 0);
  const overdueCount = pos.filter((p: any) => {
    if (p.status === 'Received' || p.status === 'Cancelled' || !p.expected_date) return false;
    const d = new Date(p.expected_date);
    d.setHours(0, 0, 0, 0);
    return d.getTime() < today.getTime();
  }).length;

  const s: Record<string, React.CSSProperties> = {
    card: { background: '#fff', borderRadius: 12, padding: '20px 24px', boxShadow: '0 1px 3px rgba(0,0,0,0.1)', marginBottom: 16 },
    badge: { padding: '3px 10px', borderRadius: 12, fontSize: 12, fontWeight: 600, color: '#fff', display: 'inline-block' },
    th: { padding: '10px 14px', fontWeight: 600, fontSize: 13, color: '#374151', textAlign: 'left' as const, borderBottom: '1px solid #e5e7eb', background: '#f9fafb' },
    td: { padding: '10px 14px', fontSize: 14, borderBottom: '1px solid #f3f4f6', verticalAlign: 'middle' as const },
    input: { width: '100%', padding: '8px 12px', border: '1.5px solid #d1d5db', borderRadius: 8, fontSize: 14, outline: 'none', boxSizing: 'border-box' as const },
    overlay: { position: 'fixed' as const, inset: 0, background: 'rgba(0,0,0,0.5)', zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 20 },
    modal: { background: '#fff', borderRadius: 16, width: '100%', maxWidth: 860, maxHeight: '90vh', overflowY: 'auto' as const, padding: 32 },
  };

  return (
    <PageLayout
      title="📋 Purchase Orders"
      action={
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
          <button
            onClick={loadLowStockSuggestions}
            disabled={lowStockLoading}
            style={{ padding: '10px 16px', background: '#f59e0b', color: '#fff', border: 'none', borderRadius: 8, fontSize: 14, fontWeight: 600, cursor: lowStockLoading ? 'not-allowed' : 'pointer' }}
          >
            {lowStockLoading ? 'Loading...' : '⚠️ Low-Stock POs'}
          </button>
          <button
            onClick={() => { resetCreateForm(); setShowCreate(true); }}
            style={{ padding: '10px 20px', background: '#3b82f6', color: '#fff', border: 'none', borderRadius: 8, fontSize: 14, fontWeight: 600, cursor: 'pointer' }}
          >
            ➕ New Purchase Order
          </button>
        </div>
      }
    >
      {pendingGrn && (
        <div style={{ ...s.card, background: '#f0fdf4', border: '1px solid #bbf7d0', display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 16, flexWrap: 'wrap' }}>
          <div>
            <div style={{ fontWeight: 700, color: '#15803d' }}>✅ Goods received — {pendingGrn.grn_number}</div>
            <div style={{ fontSize: 13, color: '#166534' }}>PO {pendingGrn.po_number} · £{pendingGrn.total.toFixed(2)} received value</div>
          </div>
          <div style={{ display: 'flex', gap: 8 }}>
            <PDFDownloadLink
              document={<GRNPdf grn={pendingGrn} businessSettings={businessSettings} />}
              fileName={`${pendingGrn.grn_number}.pdf`}
              style={{ textDecoration: 'none' }}
            >
              {({ loading: pdfLoading }) => (
                <button style={{ padding: '8px 16px', background: '#10b981', color: '#fff', border: 'none', borderRadius: 8, fontSize: 13, fontWeight: 600, cursor: 'pointer' }}>
                  {pdfLoading ? 'Preparing...' : '📥 Download GRN PDF'}
                </button>
              )}
            </PDFDownloadLink>
            <button onClick={() => setPendingGrn(null)} style={{ padding: '8px 12px', background: 'transparent', border: '1px solid #86efac', borderRadius: 8, cursor: 'pointer', fontSize: 13 }}>Dismiss</button>
          </div>
        </div>
      )}

      {/* Workflow guide */}
      <div style={{ display: 'flex', gap: 0, marginBottom: 24, background: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: 10, overflow: 'hidden' }}>
        {[
          { step: '1', label: 'Draft', desc: 'You build the order', color: '#6b7280' },
          { step: '2', label: 'Mark as Ordered', desc: 'Order sent to supplier', color: '#3b82f6' },
          { step: '3', label: 'Receive Goods', desc: 'Goods arrive at your warehouse', color: '#f59e0b' },
          { step: '4', label: 'Received', desc: 'Stock updated, cost recorded', color: '#10b981' },
        ].map((s, i) => (
          <div key={i} style={{ flex: 1, padding: '10px 14px', borderRight: i < 3 ? '1px solid #e2e8f0' : 'none', display: 'flex', alignItems: 'center', gap: 10 }}>
            <div style={{ width: 26, height: 26, borderRadius: '50%', background: s.color, color: '#fff', fontWeight: 700, fontSize: 13, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>{s.step}</div>
            <div>
              <div style={{ fontWeight: 600, fontSize: 12, color: '#1e293b' }}>{s.label}</div>
              <div style={{ fontSize: 11, color: '#64748b' }}>{s.desc}</div>
            </div>
          </div>
        ))}
      </div>

      {/* Stats */}
      <div style={{ display: 'flex', gap: 16, marginBottom: 24, flexWrap: 'wrap' }}>
        {['Draft', 'Sent', 'Partial', 'Received'].map(st => {
          const cnt = pos.filter(p => p.status === st).length;
          return (
            <div key={st} style={{ ...s.card, flex: 1, minWidth: 120, padding: '14px 20px', textAlign: 'center', marginBottom: 0, cursor: 'pointer', border: statusFilter === st ? '2px solid #3b82f6' : '1px solid #e5e7eb' }}
              onClick={() => setStatusFilter(statusFilter === st ? 'All' : st)}>
              <div style={{ fontSize: 22, fontWeight: 700, color: STATUS_COLORS[st] }}>{cnt}</div>
              <div style={{ fontSize: 12, color: '#6b7280' }}>{st}</div>
            </div>
          );
        })}
        <div style={{ ...s.card, minWidth: 180, padding: '14px 20px', marginBottom: 0 }}>
          <div style={{ fontSize: 20, fontWeight: 800, color: '#1d4ed8' }}>£{openValue.toLocaleString('en-GB', { maximumFractionDigits: 0 })}</div>
          <div style={{ fontSize: 12, color: '#6b7280' }}>Open PO Value</div>
        </div>
        <div style={{ ...s.card, minWidth: 180, padding: '14px 20px', marginBottom: 0 }}>
          <div style={{ fontSize: 20, fontWeight: 800, color: '#d97706' }}>£{partialValue.toLocaleString('en-GB', { maximumFractionDigits: 0 })}</div>
          <div style={{ fontSize: 12, color: '#6b7280' }}>Partial PO Value</div>
        </div>
        <div style={{ ...s.card, minWidth: 150, padding: '14px 20px', marginBottom: 0 }}>
          <div style={{ fontSize: 20, fontWeight: 800, color: overdueCount > 0 ? '#dc2626' : '#10b981' }}>{overdueCount}</div>
          <div style={{ fontSize: 12, color: '#6b7280' }}>Overdue POs</div>
        </div>
      </div>

      {/* Filter */}
      <div style={{ display: 'flex', gap: 8, marginBottom: 16, alignItems: 'center', flexWrap: 'wrap' }}>
        <input
          value={searchTerm}
          onChange={e => setSearchTerm(e.target.value)}
          placeholder="Search PO number, supplier, notes..."
          style={{ ...s.input, width: 280, padding: '6px 12px' }}
        />
        <label style={{ fontSize: 14, color: '#374151', fontWeight: 500 }}>Status:</label>
        <select value={statusFilter} onChange={e => setStatusFilter(e.target.value)}
          style={{ padding: '6px 12px', border: '1px solid #d1d5db', borderRadius: 8, fontSize: 14 }}>
          <option>All</option>
          <option>Draft</option>
          <option>Sent</option>
          <option>Partial</option>
          <option>Received</option>
          <option>Cancelled</option>
        </select>
        <label style={{ fontSize: 14, color: '#374151', fontWeight: 500 }}>Supplier:</label>
        <select value={supplierFilter} onChange={e => setSupplierFilter(e.target.value)}
          style={{ padding: '6px 12px', border: '1px solid #d1d5db', borderRadius: 8, fontSize: 14 }}>
          <option value="All">All</option>
          {suppliers.map((s: any) => <option key={s.id} value={String(s.id)}>{s.name}</option>)}
        </select>
        {(statusFilter !== 'All' || supplierFilter !== 'All' || searchTerm) && (
          <button
            onClick={() => { setStatusFilter('All'); setSupplierFilter('All'); setSearchTerm(''); }}
            style={{ fontSize: 13, color: '#6b7280', background: 'none', border: 'none', cursor: 'pointer' }}
          >
            ✕ Clear
          </button>
        )}
      </div>

      {/* Table */}
      {loading ? <div style={{ textAlign: 'center', padding: 60, color: '#6b7280' }}>Loading...</div> : (
        <>
        <ListPagination
          total={pos.length}
          filtered={filtered.length}
          page={safeListPage}
          pages={poPages}
          onPageChange={setListPage}
          noun="purchase orders"
        />
        <div className="list-table-panel">
        <div style={{ ...s.card, padding: 0, overflow: 'hidden' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead>
              <tr>
                {['PO Number', 'Supplier', 'Status', 'Items', 'Total', 'Expected', 'Lead Time', 'Actions'].map(h => (
                  <th key={h} style={s.th}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {paginatedPOs.length === 0 ? (
                <tr><td colSpan={8} style={{ textAlign: 'center', padding: 48, color: '#9ca3af' }}>No purchase orders found. Create your first one.</td></tr>
              ) : paginatedPOs.map(po => (
                <tr key={po.id} style={{ background: '#fff' }}>
                  <td style={s.td}><strong style={{ color: '#3b82f6', cursor: 'pointer' }} onClick={() => openView(po)}>{po.po_number}</strong></td>
                  <td style={s.td}>{po.supplier_name || <span style={{ color: '#9ca3af' }}>—</span>}</td>
                  <td style={s.td}><span style={{ ...s.badge, background: STATUS_COLORS[po.status] || '#6b7280' }}>{po.status}</span></td>
                  <td style={s.td}>{po.item_count}</td>
                  <td style={s.td}>£{parseFloat(po.total || 0).toFixed(2)}</td>
                  <td style={s.td}>{po.expected_date ? new Date(po.expected_date).toLocaleDateString('en-GB') : <span style={{ color: '#9ca3af' }}>—</span>}</td>
                  <td style={s.td}>
                    {po.expected_date ? (() => {
                      const exp = new Date(po.expected_date);
                      exp.setHours(0, 0, 0, 0);
                      const days = Math.ceil((exp.getTime() - today.getTime()) / 86400000);
                      const isClosed = po.status === 'Received' || po.status === 'Cancelled';
                      const color = isClosed ? '#6b7280' : days < 0 ? '#dc2626' : days <= 2 ? '#d97706' : '#2563eb';
                      const text = isClosed ? 'Closed' : days < 0 ? `${Math.abs(days)}d overdue` : days === 0 ? 'Due today' : `${days}d left`;
                      return <span style={{ fontSize: 12, fontWeight: 700, color }}>{text}</span>;
                    })() : <span style={{ color: '#9ca3af' }}>—</span>}
                  </td>
                  <td style={s.td}>
                    <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                      <ActionButton variant="view" label="View" onClick={() => openView(po)} />
                      {(po.status === 'Draft' || po.status === 'Sent' || po.status === 'Partial') && (
                        <button onClick={() => openView(po).then(() => setShowReceive(true))}
                          style={{ padding: '4px 10px', background: '#10b981', color: '#fff', border: 'none', borderRadius: 6, fontSize: 12, cursor: 'pointer', fontWeight: 600 }}>
                          📦 Receive Goods
                        </button>
                      )}
                      {po.status === 'Draft' && (
                        <>
                          <button onClick={() => openEditDraft(po)}
                            style={{ padding: '4px 10px', background: '#6366f1', color: '#fff', border: 'none', borderRadius: 6, fontSize: 12, cursor: 'pointer' }}>
                            ✏️ Edit
                          </button>
                          <button onClick={() => updateStatus(po, 'Sent')}
                            style={{ padding: '4px 10px', background: '#3b82f6', color: '#fff', border: 'none', borderRadius: 6, fontSize: 12, cursor: 'pointer' }}>
                            ✉️ Mark as Ordered
                          </button>
                          <ActionButton variant="delete" label="Delete" onClick={() => deletePO(po)} />
                        </>
                      )}
                      {po.status !== 'Cancelled' && po.status !== 'Received' && po.status !== 'Draft' && (
                        <button onClick={() => cancelPOFromList(po)}
                          style={{ padding: '4px 10px', background: '#ef4444', color: '#fff', border: 'none', borderRadius: 6, fontSize: 12, cursor: 'pointer' }}>
                          Cancel
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        </div>

        <div className="list-mobile-cards">
          {paginatedPOs.length === 0 ? (
            <div style={{ textAlign: 'center', padding: 48, color: '#9ca3af' }}>No purchase orders found. Create your first one.</div>
          ) : paginatedPOs.map(po => (
            <div key={po.id} className="list-mobile-card">
              <div className="list-mobile-card-header">
                <div style={{ minWidth: 0, flex: 1 }}>
                  <div className="list-mobile-card-title" style={{ color: '#3b82f6', cursor: 'pointer' }} onClick={() => openView(po)}>{po.po_number}</div>
                  <div className="list-mobile-card-meta">{po.supplier_name || '—'}</div>
                </div>
                <span style={{ ...s.badge, background: STATUS_COLORS[po.status] || '#6b7280' }}>{po.status}</span>
              </div>
              <div className="list-mobile-card-info">
                <div><div className="list-mobile-card-label">Items</div>{po.item_count}</div>
                <div><div className="list-mobile-card-label">Total</div>£{parseFloat(po.total || 0).toFixed(2)}</div>
                <div><div className="list-mobile-card-label">Expected</div>{po.expected_date ? new Date(po.expected_date).toLocaleDateString('en-GB') : '—'}</div>
              </div>
              <div className="list-mobile-actions">
                <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                  <ActionButton variant="view" label="View" onClick={() => openView(po)} />
                  {(po.status === 'Draft' || po.status === 'Sent' || po.status === 'Partial') && (
                    <button onClick={() => openView(po).then(() => setShowReceive(true))}
                      style={{ padding: '4px 10px', background: '#10b981', color: '#fff', border: 'none', borderRadius: 6, fontSize: 12, cursor: 'pointer', fontWeight: 600 }}>
                      📦 Receive
                    </button>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>
        </>
      )}

      {/* Create Modal */}
      {showCreate && (
        <div style={s.overlay} onClick={() => { setShowCreate(false); resetCreateForm(); }}>
          <div style={s.modal} onClick={e => e.stopPropagation()}>
            <h2 style={{ margin: '0 0 24px', fontSize: 20, fontWeight: 700 }}>
              {editingPoId ? 'Edit Purchase Order' : 'New Purchase Order'}
            </h2>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16, marginBottom: 16 }}>
              <div>
                <label style={{ fontSize: 13, fontWeight: 600, display: 'block', marginBottom: 6 }}>Supplier</label>
                <select
                  value={form.supplier_id}
                  onChange={e => {
                    if (e.target.value) applySupplierDefaults(e.target.value);
                    else setForm(f => ({ ...f, supplier_id: '', supplier_name: '' }));
                  }}
                  style={s.input}
                >
                  <option value="">-- Select Supplier --</option>
                  {suppliers.map((sup: any) => <option key={sup.id} value={sup.id}>{sup.name}</option>)}
                </select>
                {selectedPaymentTerms && (
                  <div style={{ fontSize: 12, color: '#6b7280', marginTop: 6 }}>
                    Payment terms: <strong>{selectedPaymentTerms}</strong>
                    {selectedSupplier && Number(selectedSupplier.lead_time_days ?? selectedSupplier.leadTimeDays ?? 0) > 0 && (
                      <> · Lead time: {Number(selectedSupplier.lead_time_days ?? selectedSupplier.leadTimeDays)} days</>
                    )}
                  </div>
                )}
              </div>
              <div>
                <label style={{ fontSize: 13, fontWeight: 600, display: 'block', marginBottom: 6 }}>Expected Delivery</label>
                <input type="date" value={form.expected_date} onChange={e => setForm(f => ({ ...f, expected_date: e.target.value }))} style={s.input} />
              </div>
            </div>
            <div style={{ marginBottom: 20 }}>
              <label style={{ fontSize: 13, fontWeight: 600, display: 'block', marginBottom: 6 }}>Notes</label>
              <textarea value={form.notes} onChange={e => setForm(f => ({ ...f, notes: e.target.value }))}
                style={{ ...s.input, height: 60, resize: 'vertical' }} placeholder="Optional notes..." />
            </div>

            <h3 style={{ fontSize: 15, fontWeight: 600, marginBottom: 12 }}>Items</h3>

            {/* Supplier product filter notice */}
            {form.supplier_id && (
              <div style={{ marginBottom: 10, padding: '8px 12px', borderRadius: 8, background: supplierLinkedProducts.length > 0 ? '#f0fdf4' : '#fffbeb', border: `1px solid ${supplierLinkedProducts.length > 0 ? '#bbf7d0' : '#fde68a'}`, fontSize: 13, color: supplierLinkedProducts.length > 0 ? '#15803d' : '#92400e' }}>
                {supplierLinkedProducts.length > 0
                  ? `✅ Showing ${supplierLinkedProducts.length} product${supplierLinkedProducts.length !== 1 ? 's' : ''} linked to ${selectedSupplierName}`
                  : `⚠️ No products are linked to ${selectedSupplierName} yet — showing all ${products.length} products. To link, edit each product and set its Supplier field.`}
              </div>
            )}
            <table style={{ width: '100%', borderCollapse: 'collapse', marginBottom: 12 }}>
              <thead>
                <tr>
                  {['Product', 'SKU', 'Qty', 'Unit Cost (£)', ''].map(h => (
                    <th key={h} style={{ ...s.th, fontSize: 12 }}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {formItems.map((item, idx) => (
                  <tr key={idx}>
                    <td style={s.td}>
                      <select value={item.product_id} onChange={e => selectProduct(idx, e.target.value)} style={{ ...s.input, marginBottom: 4 }}>
                        <option value="">-- Product --</option>
                        {itemProductList.map((p: any) => <option key={p.id} value={p.id}>{p.name}</option>)}
                      </select>
                      {!item.product_id && (
                        <input value={item.product_name} onChange={e => setItemField(idx, 'product_name', e.target.value)}
                          style={s.input} placeholder="Or type name..." />
                      )}
                      {!item.product_id && item.product_name && (
                        <div style={{ fontSize: 11, color: '#f59e0b', marginTop: 3 }}>⚠️ Not linked to a product — stock won't auto-update on receive</div>
                      )}
                    </td>
                    <td style={s.td}><input value={item.sku} onChange={e => setItemField(idx, 'sku', e.target.value)} style={s.input} placeholder="SKU" /></td>
                    <td style={s.td}><input type="number" min="0" value={item.quantity_ordered} onChange={e => setItemField(idx, 'quantity_ordered', e.target.value)} style={s.input} placeholder="0" /></td>
                    <td style={s.td}><input type="number" min="0" step="0.01" value={item.unit_cost} onChange={e => setItemField(idx, 'unit_cost', e.target.value)} style={s.input} placeholder="0.00" /></td>
                    <td style={s.td}>
                      {formItems.length > 1 && (
                        <button onClick={() => setFormItems(prev => prev.filter((_, i) => i !== idx))}
                          style={{ background: '#ef4444', color: '#fff', border: 'none', borderRadius: 6, padding: '4px 8px', cursor: 'pointer' }}>✕</button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            <button onClick={() => setFormItems(prev => [...prev, { product_id: '', product_name: '', sku: '', quantity_ordered: '', unit_cost: '' }])}
              style={{ marginBottom: 24, fontSize: 13, background: '#f3f4f6', border: '1px solid #d1d5db', borderRadius: 8, padding: '6px 14px', cursor: 'pointer' }}>
              + Add Item
            </button>

            {/* Total */}
            <div style={{ textAlign: 'right', marginBottom: 20, fontSize: 15, fontWeight: 700 }}>
              Total: £{formItems.reduce((sum, i) => sum + (parseFloat(i.quantity_ordered || 0) * parseFloat(i.unit_cost || 0)), 0).toFixed(2)}
            </div>

            <div style={{ display: 'flex', gap: 12, justifyContent: 'flex-end' }}>
              <button onClick={() => { setShowCreate(false); resetCreateForm(); }} style={{ padding: '10px 24px', background: '#f3f4f6', border: '1px solid #d1d5db', borderRadius: 8, cursor: 'pointer', fontSize: 14 }}>Cancel</button>
              <button onClick={submitCreate} disabled={saving}
                style={{ padding: '10px 24px', background: '#3b82f6', color: '#fff', border: 'none', borderRadius: 8, cursor: saving ? 'not-allowed' : 'pointer', fontSize: 14, fontWeight: 600 }}>
                {saving ? (editingPoId ? 'Saving...' : 'Creating...') : (editingPoId ? 'Save Changes' : 'Create PO')}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* View Modal */}
      {showView && activePO && !showReceive && (
        <div style={s.overlay} onClick={() => setShowView(false)}>
          <div style={s.modal} onClick={e => e.stopPropagation()}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 24 }}>
              <h2 style={{ margin: 0, fontSize: 20, fontWeight: 700 }}>PO: {activePO.po_number}</h2>
              <span style={{ ...s.badge, background: STATUS_COLORS[activePO.status] || '#6b7280', padding: '4px 14px', fontSize: 13 }}>{activePO.status}</span>
            </div>
            {activePO.status === 'Cancelled' && (
              <div style={{ marginBottom: 16, padding: '12px 14px', background: '#fee2e2', border: '1px solid #fca5a5', borderRadius: 8, fontSize: 13, color: '#991b1b' }}>
                ❌ This PO has been cancelled. Any received stock has been reversed.
              </div>
            )}
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 12, marginBottom: 20, padding: '16px', background: '#f9fafb', borderRadius: 10 }}>
              <div><div style={{ fontSize: 11, color: '#9ca3af', fontWeight: 600, textTransform: 'uppercase' }}>Supplier</div><div style={{ fontWeight: 600 }}>{activePO.supplier_name || '—'}</div></div>
              <div><div style={{ fontSize: 11, color: '#9ca3af', fontWeight: 600, textTransform: 'uppercase' }}>Expected</div><div style={{ fontWeight: 600 }}>{activePO.expected_date ? new Date(activePO.expected_date).toLocaleDateString('en-GB') : '—'}</div></div>
              <div><div style={{ fontSize: 11, color: '#9ca3af', fontWeight: 600, textTransform: 'uppercase' }}>Total</div><div style={{ fontWeight: 700, color: '#3b82f6' }}>£{parseFloat(activePO.total || 0).toFixed(2)}</div></div>
            </div>
            {activePO.notes && <div style={{ marginBottom: 16, padding: '10px 14px', background: '#fffbeb', borderRadius: 8, fontSize: 13, color: '#92400e' }}>{activePO.notes}</div>}
            <table style={{ width: '100%', borderCollapse: 'collapse', marginBottom: 24 }}>
              <thead>
                <tr>{['Product', 'SKU', 'Ordered', 'Received', 'Remaining', 'Unit Cost', 'Line Total'].map(h => <th key={h} style={s.th}>{h}</th>)}</tr>
              </thead>
              <tbody>
                {(activePO.items || []).map((item: any) => {
                  const remaining = parseFloat(item.quantity_ordered) - parseFloat(item.quantity_received || 0);
                  return (
                    <tr key={item.id}>
                      <td style={s.td}>
                        <strong>{item.product_name}</strong>
                        {!item.product_id && (
                          <div style={{ fontSize: 11, color: '#f59e0b', marginTop: 2 }}>⚠️ Unlinked — stock won't update on receive</div>
                        )}
                      </td>
                      <td style={s.td}>{item.sku || '—'}</td>
                      <td style={s.td}>{parseFloat(item.quantity_ordered)}</td>
                      <td style={s.td}><span style={{ color: parseFloat(item.quantity_received) > 0 ? '#10b981' : '#9ca3af' }}>{parseFloat(item.quantity_received || 0)}</span></td>
                      <td style={s.td}><span style={{ color: remaining > 0 ? '#f59e0b' : '#10b981', fontWeight: 600 }}>{remaining}</span></td>
                      <td style={s.td}>£{parseFloat(item.unit_cost || 0).toFixed(2)}</td>
                      <td style={s.td}>£{(parseFloat(item.quantity_ordered) * parseFloat(item.unit_cost || 0)).toFixed(2)}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>

            <div style={{ marginBottom: 20 }}>
              <div style={{ fontSize: 14, fontWeight: 700, color: '#374151', marginBottom: 10 }}>Receipt History (GRN)</div>
              {groupReceiptBatches(activePO).length > 0 ? (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                  {groupReceiptBatches(activePO).map(batch => (
                    <div key={batch.key} style={{ border: '1px solid #e5e7eb', borderRadius: 10, overflow: 'hidden', background: '#fff' }}>
                      <div style={{ padding: '10px 14px', background: '#f0fdf4', borderBottom: '1px solid #bbf7d0', display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 8 }}>
                        <div>
                          <div style={{ fontWeight: 700, fontSize: 13, color: '#15803d' }}>{batch.grnNumber.startsWith('legacy-') ? 'Receipt' : batch.grnNumber}</div>
                          <div style={{ fontSize: 12, color: '#6b7280' }}>
                            {new Date(batch.data.received_at || '').toLocaleDateString('en-GB')} · {batch.data.received_by_name || 'User'} · £{batch.data.total.toFixed(2)}
                          </div>
                        </div>
                        <PDFDownloadLink
                          document={<GRNPdf grn={batch.data} businessSettings={businessSettings} />}
                          fileName={`${batch.grnNumber}.pdf`}
                          style={{ textDecoration: 'none' }}
                        >
                          {({ loading: pdfLoading }) => (
                            <button style={{ padding: '6px 12px', background: '#10b981', color: '#fff', border: 'none', borderRadius: 6, fontSize: 12, fontWeight: 600, cursor: 'pointer' }}>
                              {pdfLoading ? '...' : '📥 GRN PDF'}
                            </button>
                          )}
                        </PDFDownloadLink>
                      </div>
                      {batch.receipts.map((r: any) => (
                        <div key={r.id} style={{ padding: '8px 14px', borderBottom: '1px solid #f3f4f6', fontSize: 12, color: '#374151' }}>
                          {r.product_name} — {parseFloat(r.quantity_received || 0)} units
                          {r.unit_cost != null && ` @ £${parseFloat(r.unit_cost).toFixed(2)}`}
                        </div>
                      ))}
                    </div>
                  ))}
                </div>
              ) : (
                <div style={{ fontSize: 13, color: '#9ca3af', padding: '10px 2px' }}>No receipts recorded yet.</div>
              )}
            </div>

            <div style={{ display: 'flex', gap: 12, justifyContent: 'flex-end', flexWrap: 'wrap' }}>
              <button onClick={() => setShowView(false)} style={{ padding: '10px 24px', background: '#f3f4f6', border: '1px solid #d1d5db', borderRadius: 8, cursor: 'pointer', fontSize: 14 }}>Close</button>
              {activePO.status === 'Draft' && (
                <button onClick={() => openEditDraft(activePO)}
                  style={{ padding: '10px 24px', background: '#6366f1', color: '#fff', border: 'none', borderRadius: 8, cursor: 'pointer', fontSize: 14, fontWeight: 600 }}>
                  ✏️ Edit Draft
                </button>
              )}
              <button onClick={() => setShowSendEmail(true)} style={{ padding: '10px 24px', background: '#06b6d4', color: '#fff', border: 'none', borderRadius: 8, cursor: 'pointer', fontSize: 14, fontWeight: 600 }}>📧 Send to Supplier</button>
              <PDFDownloadLink
                document={<POPdf po={activePO} businessSettings={businessSettings} />}
                fileName={`po-${activePO.po_number}.pdf`}
                style={{ textDecoration: 'none' }}
              >
                {({ loading: pdfLoading }) => (
                  <button
                    style={{ padding: '10px 24px', background: '#667eea', color: '#fff', border: 'none', borderRadius: 8, cursor: 'pointer', fontSize: 14, fontWeight: 600 }}
                    disabled={pdfLoading}
                  >
                    {pdfLoading ? '📄 Preparing PDF...' : '📥 Download PDF'}
                  </button>
                )}
              </PDFDownloadLink>
              {(activePO.status === 'Draft' || activePO.status === 'Sent' || activePO.status === 'Partial') && (
                <button onClick={() => { initReceiveForm(activePO); setShowReceive(true); }}
                  style={{ padding: '10px 24px', background: '#10b981', color: '#fff', border: 'none', borderRadius: 8, cursor: 'pointer', fontSize: 14, fontWeight: 600 }}>
                  ✅ Receive Goods
                </button>
              )}
              {activePO.status !== 'Draft' && activePO.status !== 'Cancelled' && (
                <button onClick={submitCancel} disabled={saving}
                  style={{ padding: '10px 24px', background: '#ef4444', color: '#fff', border: 'none', borderRadius: 8, cursor: saving ? 'not-allowed' : 'pointer', fontSize: 14, fontWeight: 600, opacity: saving ? 0.6 : 1 }}>
                  ❌ Cancel PO
                </button>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Receive Modal */}
      {showReceive && activePO && (
        <div style={s.overlay} onClick={() => { setShowReceive(false); }}>
          <div style={s.modal} onClick={e => e.stopPropagation()}>
            <h2 style={{ margin: '0 0 8px', fontSize: 20, fontWeight: 700 }}>Receive Goods — {activePO.po_number}</h2>
            <p style={{ color: '#6b7280', fontSize: 14, marginBottom: 20 }}>Enter quantities and adjust unit costs if the invoice differs from the PO. A GRN will be generated on confirm.</p>
            <table style={{ width: '100%', borderCollapse: 'collapse', marginBottom: 24 }}>
              <thead>
                <tr>{['Product', 'Ordered', 'Already Received', 'Receive Now', 'Unit Cost (£)'].map(h => <th key={h} style={s.th}>{h}</th>)}</tr>
              </thead>
              <tbody>
                {(activePO.items || []).map((item: any) => {
                  const remaining = parseFloat(item.quantity_ordered) - parseFloat(item.quantity_received || 0);
                  return (
                    <tr key={item.id}>
                      <td style={s.td}>
                        <strong>{item.product_name}</strong>
                        {!item.product_id && (
                          <div style={{ fontSize: 11, color: '#f59e0b', marginTop: 2 }}>⚠️ Unlinked — stock won't update</div>
                        )}
                      </td>
                      <td style={s.td}>{parseFloat(item.quantity_ordered)}</td>
                      <td style={s.td}>{parseFloat(item.quantity_received || 0)}</td>
                      <td style={s.td}>
                        {remaining > 0 ? (
                          <input type="number" min="0" max={remaining} step="1"
                            value={receiveQtys[item.id] || ''}
                            onChange={e => setReceiveQtys(prev => ({ ...prev, [item.id]: e.target.value }))}
                            style={{ ...s.input, width: 90 }} placeholder={`max ${remaining}`} />
                        ) : <span style={{ color: '#10b981', fontWeight: 600 }}>✅ Complete</span>}
                      </td>
                      <td style={s.td}>
                        {remaining > 0 ? (
                          <input type="number" min="0" step="0.01"
                            value={receiveCosts[item.id] ?? ''}
                            onChange={e => setReceiveCosts(prev => ({ ...prev, [item.id]: e.target.value }))}
                            style={{ ...s.input, width: 90 }} placeholder="0.00" />
                        ) : <span style={{ color: '#9ca3af' }}>—</span>}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
            <div style={{ display: 'flex', gap: 12, justifyContent: 'flex-end' }}>
              <button onClick={() => setShowReceive(false)} style={{ padding: '10px 24px', background: '#f3f4f6', border: '1px solid #d1d5db', borderRadius: 8, cursor: 'pointer', fontSize: 14 }}>Cancel</button>
              <button onClick={submitReceive} disabled={saving}
                style={{ padding: '10px 24px', background: '#10b981', color: '#fff', border: 'none', borderRadius: 8, cursor: saving ? 'not-allowed' : 'pointer', fontSize: 14, fontWeight: 600 }}>
                {saving ? 'Saving...' : '✅ Confirm Receipt'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Send Email Modal */}
      {showSendEmail && activePO && (
        <div style={s.overlay} onClick={() => { setShowSendEmail(false); }}>
          <div style={s.modal} onClick={e => e.stopPropagation()}>
            <h2 style={{ margin: '0 0 20px', fontSize: 20, fontWeight: 700 }}>Send PO to Supplier</h2>
            
            <div style={{ marginBottom: 16 }}>
              <label style={{ display: 'block', marginBottom: 6, fontSize: 13, fontWeight: 600, color: '#374151' }}>Supplier Email *</label>
              <input
                type="email"
                value={emailForm.supplier_email}
                onChange={(e) => setEmailForm({ ...emailForm, supplier_email: e.target.value })}
                placeholder="Enter supplier email address"
                style={s.input}
              />
            </div>

            <div style={{ marginBottom: 16 }}>
              <label style={{ display: 'block', marginBottom: 6, fontSize: 13, fontWeight: 600, color: '#374151' }}>Message (Optional)</label>
              <textarea
                value={emailForm.message}
                onChange={(e) => setEmailForm({ ...emailForm, message: e.target.value })}
                placeholder="Add a personal message to include in the email..."
                rows={4}
                style={{ ...s.input, fontFamily: 'Arial,sans-serif', resize: 'vertical' }}
              />
            </div>

            <div style={{ display: 'flex', gap: 12, justifyContent: 'flex-end' }}>
              <button onClick={() => setShowSendEmail(false)} disabled={saving} style={{ padding: '10px 24px', background: '#f3f4f6', border: '1px solid #d1d5db', borderRadius: 8, cursor: 'pointer', fontSize: 14 }}>Cancel</button>
              <button onClick={submitSendEmail} disabled={saving} style={{ padding: '10px 24px', background: '#06b6d4', color: '#fff', border: 'none', borderRadius: 8, cursor: saving ? 'not-allowed' : 'pointer', fontSize: 14, fontWeight: 600 }}>
                {saving ? 'Sending...' : '📧 Send Email'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Low-Stock Bulk Draft Modal */}
      {showLowStock && (
        <div style={s.overlay} onClick={() => setShowLowStock(false)}>
          <div style={{ ...s.modal, maxWidth: 920 }} onClick={e => e.stopPropagation()}>
            <h2 style={{ margin: '0 0 8px', fontSize: 20, fontWeight: 700 }}>⚠️ Low-Stock Purchase Orders</h2>
            <p style={{ color: '#6b7280', fontSize: 14, marginBottom: 20 }}>
              Products at or below reorder point, grouped by supplier. Creates one Draft PO per selected supplier.
            </p>

            {lowStockGroups.length === 0 ? (
              <div style={{ padding: 40, textAlign: 'center', color: '#9ca3af' }}>
                No low-stock products with a linked supplier found.
              </div>
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 12, marginBottom: 24, maxHeight: '50vh', overflowY: 'auto' }}>
                {lowStockGroups.map((group: any) => {
                  const key = String(group.supplier_id);
                  const selected = selectedGroupKeys.has(key);
                  const groupTotal = (group.items || []).reduce(
                    (sum: number, i: any) => sum + (parseFloat(i.quantity_ordered) * parseFloat(i.unit_cost || 0)), 0
                  );
                  return (
                    <div key={key} style={{ border: `2px solid ${selected ? '#3b82f6' : '#e5e7eb'}`, borderRadius: 10, overflow: 'hidden' }}>
                      <div
                        style={{ padding: '12px 16px', background: selected ? '#eff6ff' : '#f9fafb', cursor: 'pointer', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}
                        onClick={() => {
                          setSelectedGroupKeys(prev => {
                            const next = new Set(prev);
                            if (next.has(key)) next.delete(key); else next.add(key);
                            return next;
                          });
                        }}
                      >
                        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                          <input type="checkbox" checked={selected} readOnly style={{ width: 16, height: 16 }} />
                          <div>
                            <div style={{ fontWeight: 700 }}>{group.supplier_name}</div>
                            <div style={{ fontSize: 12, color: '#6b7280' }}>{group.items.length} product(s) · est. £{groupTotal.toFixed(2)}</div>
                          </div>
                        </div>
                      </div>
                      <div style={{ padding: '8px 16px' }}>
                        {(group.items || []).slice(0, 5).map((item: any) => (
                          <div key={item.product_id} style={{ fontSize: 12, color: '#374151', padding: '4px 0', borderBottom: '1px solid #f3f4f6' }}>
                            {item.product_name} — stock {item.stock}/{item.min_stock} → order {item.quantity_ordered} @ £{parseFloat(item.unit_cost || 0).toFixed(2)}
                          </div>
                        ))}
                        {group.items.length > 5 && (
                          <div style={{ fontSize: 11, color: '#9ca3af', paddingTop: 6 }}>+{group.items.length - 5} more items</div>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            )}

            <div style={{ display: 'flex', gap: 12, justifyContent: 'flex-end' }}>
              <button onClick={() => setShowLowStock(false)} style={{ padding: '10px 24px', background: '#f3f4f6', border: '1px solid #d1d5db', borderRadius: 8, cursor: 'pointer', fontSize: 14 }}>Cancel</button>
              <button onClick={submitBulkDraft} disabled={saving || lowStockGroups.length === 0}
                style={{ padding: '10px 24px', background: '#f59e0b', color: '#fff', border: 'none', borderRadius: 8, cursor: saving ? 'not-allowed' : 'pointer', fontSize: 14, fontWeight: 600 }}>
                {saving ? 'Creating...' : `Create ${selectedGroupKeys.size} Draft PO${selectedGroupKeys.size !== 1 ? 's' : ''}`}
              </button>
            </div>
          </div>
        </div>
      )}
    </PageLayout>
  );
};

export default PurchaseOrders;
